import {get, set, tail, isEmpty, cloneDeep, isObject} from 'lodash';
import {useCallback, useMemo, useState} from 'react';

import {TreeNode, TreeNodeState} from 'components';
import {NodePath} from 'types';

function hasStateWithValue(state: TreeNodeState, name: keyof TreeNodeState, value: any): boolean {
    let result = false;
    for (const key in state) {
        if (key === name && value === state[key]) {
            return true;
        } else if (isObject(state[key])) {
            result = hasStateWithValue(state[key], name, value);
            if (result) {
                return result;
            }
        }
    }

    return result;
}

export function useTree<T>(initialValue: TreeNode<T>) {
    const [tree, setTree] = useState(cloneDeep(initialValue));
    const [nodeState, setNodeState] = useState<TreeNodeState>({});
    const isInvalid = useMemo(() => hasStateWithValue(nodeState, 'error', true), [nodeState]);

    const getFullPath = useCallback((path: NodePath): (number | string)[] => {
        const fullPath = [];
        const tailedPath = tail(path);
        for (const index of tailedPath) {
            fullPath.push('children', index);
        }
        return fullPath;
    }, []);

    const updateNodeState = useCallback((path: NodePath, value: Partial<TreeNodeState>) => {
        setNodeState((prev) => {
            const prevState = get(prev, path);
            set(prev, path, {...prevState, ...value});
            return {...prev};
        });
    }, []);

    const getFullNodeValuePath = useCallback((path: NodePath): (number | string)[] => {
        const fullPath = getFullPath(path);
        return [...fullPath, 'value'];
    }, [getFullPath]);

    const getNode = useCallback((path: NodePath): TreeNode<T> => {
        const fullPath = getFullPath(path);
        if (isEmpty(fullPath)) {
            return tree;
        } else {
            return get(tree, fullPath);
        }
    }, [tree, getFullPath]);

    const getNodeValue = useCallback((path: NodePath): T => {
        const node = getNode(path);
        return node?.value;
    }, [getNode]);

    const setNodeValue = useCallback((path: NodePath, value: T) => {
        setTree((prevTree) => {
            set(prevTree, getFullNodeValuePath(path), value);
            return cloneDeep(prevTree);
        });
    }, [getFullNodeValuePath]);

    const mergeNodeValue = useCallback((path: NodePath, value: Partial<T>) => {
        const node = getNode(path);
        setNodeValue(path, {...node?.value, ...value});
    }, [getNode, setNodeValue]);

    const removeNode = useCallback((path: NodePath) => {
        const [childIndex] = path.slice(-1);
        const parentPath: NodePath = path.slice(0, -1);

        setTree((prevTree) => {
            if (isEmpty(parentPath)) {
                return null;
            }
            const parent = getNode(parentPath);
            parent.children.splice(childIndex, 1);
            return cloneDeep(prevTree);
        });

        setNodeState((prevState) => {
            if (isEmpty(parentPath)) {
                return {};
            }

            set(prevState, path, undefined);
            return cloneDeep(prevState);
        });
    }, [getNode]);

    const removeChildNodes = useCallback((path: NodePath) => {
        setTree((prev) => {
            const node = getNode(path);
            node.children = [];
            return cloneDeep(prev);
        });

        setNodeState((prevState) => {
            set(prevState, path, undefined);
            return cloneDeep(prevState);
        });
    }, [getNode]);

    const addNode = useCallback((parentPath: NodePath, value: any) => {
        setTree((prevTree) => {
            const node = getNode(parentPath);
            if (!node.children) {
                node.children = [];
            }

            node.children.push({value, children: []});
            return cloneDeep(prevTree);
        });
    }, [getNode]);

    return {
        tree, nodeState, updateNodeState, getNode, isInvalid,
        removeNode, addNode, getNodeValue, mergeNodeValue, removeChildNodes
    };
}